import React, { useRef, useState, useEffect } from 'react';
import TemplateNameField from './TemplateNameField';
import TemplateCategoryField from './TemplateCategoryField';
import TemplateMessageInput from './TemplateMessageInput';
import { MessageCharacterCount } from '@/components/sms/shared/counters';
import PersonalizationButtons from './PersonalizationButtons';
import MessagePreview from './MessagePreview';
import ErrorAlert from './ErrorAlert';
import { OPT_OUT_MESSAGE, getOptOutReceivedLength } from '@/utils/smsUtils';
const MAX_SINGLE_SMS_LENGTH = 160;
const SEGMENT_OVERHEAD = 3; // Characters used for segmenting

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface TemplateFormManagerProps {
  isSubmitting: boolean;
  onTemplateChange: (template: { name: string; content: string; category: string }) => void;
  validateTemplate: () => boolean;
  error: string | null;
  restaurantId?: string;
  initialContent?: string;
  initialName?: string;
  initialCategory?: string;
  restaurants?: RestaurantDetails[];
}

const TemplateFormManager: React.FC<TemplateFormManagerProps> = ({
  isSubmitting,
  onTemplateChange,
  validateTemplate,
  error,
  restaurantId,
  initialContent,
  initialName,
  initialCategory,
  restaurants
}) => {
  const [template, setTemplate] = useState({
    name: initialName || '',
    content: initialContent || '',
    category: initialCategory || 'promotional'
  });
  
  // Update template when initial values change
  useEffect(() => {
    console.log("TemplateFormManager initial values changed:", {
      initialName,
      initialContent,
      initialCategory
    });
    
    const updatedTemplate = {
      name: initialName || '',
      content: initialContent || '',
      category: initialCategory || 'promotional'
    };
    
    setTemplate(updatedTemplate);
    // Only call onTemplateChange on initial mount or when initial values actually change
    // Remove onTemplateChange from dependencies to prevent circular dependency
    onTemplateChange(updatedTemplate);
  }, [initialName, initialContent, initialCategory]);
  
  // Reference to the textarea for cursor positioning
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Calculate character count including opt-out message for display purposes only
  const messageWithOptOut = template.content 
    ? `${template.content}${OPT_OUT_MESSAGE}`
    : "";
  
  const characterCount = template.content.length; // Use just message length for the component
  
  // Calculate SMS segments using useMemo to prevent unnecessary recalculations
  const smsSegments = React.useMemo(() => {
    const calculateSmsSegments = (count: number) => {
      if (count <= MAX_SINGLE_SMS_LENGTH) return 1;
      // For multi-segment messages, each segment has a small overhead
      return Math.ceil((count + SEGMENT_OVERHEAD) / MAX_SINGLE_SMS_LENGTH);
    };
    return calculateSmsSegments(messageWithOptOut.length);
  }, [messageWithOptOut.length]);
  
  // Debug logging only when content actually changes (not on every render)
  useEffect(() => {
    if (template.content) {
      const optOutReceivedLength = getOptOutReceivedLength(); // 22 chars (Kudosity system)
      console.log(`Template content length: ${template.content.length}`);
      console.log(`Opt-out sent: ${OPT_OUT_MESSAGE.length} chars, received: ${optOutReceivedLength} chars`);
      console.log(`Combined message length: ${messageWithOptOut.length}`);
      console.log(`SMS segments: ${smsSegments}`);
    }
  }, [template.content, messageWithOptOut.length, smsSegments]);
  
  // Insert personalization variable at cursor position
  const handleInsertPersonalization = (variable: string) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Combine the text before and after cursor with the variable
    const newContent = 
      template.content.substring(0, start) + 
      variable + 
      template.content.substring(end);
    
    // Update the content
    const newTemplate = { ...template, content: newContent };
    setTemplate(newTemplate);
    onTemplateChange(newTemplate);
    
    // Wait for next tick to set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        const newPosition = start + variable.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleFieldChange = (field: keyof typeof template, value: string) => {
    const newTemplate = { ...template, [field]: value };
    setTemplate(newTemplate);
    onTemplateChange(newTemplate);
  };

  return (
    <div className="grid gap-4 py-4">
      <ErrorAlert message={error} />
      
      <TemplateNameField
        value={template.name}
        onChange={(value) => handleFieldChange('name', value)}
        disabled={isSubmitting}
      />
      
      <TemplateCategoryField
        value={template.category}
        onChange={(value) => handleFieldChange('category', value)}
        disabled={isSubmitting}
        restaurantId={restaurantId}
      />
      
      <div className="space-y-4">
        <TemplateMessageInput
          content={template.content}
          onChange={(value) => handleFieldChange('content', value)}
          disabled={isSubmitting}
          characterCount={characterCount}
          smsSegments={smsSegments}
          maxLength={MAX_SINGLE_SMS_LENGTH}
          textareaRef={textareaRef}
        />
        
        <PersonalizationButtons
          onInsert={handleInsertPersonalization}
          disabled={isSubmitting}
          restaurants={restaurants}
          messageContent={template.content}
        />
      </div>
      
      <MessagePreview 
        message={template.content}
        optOutMessage={OPT_OUT_MESSAGE}
      />
    </div>
  );
};

export default TemplateFormManager;
